declare var google: any;
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class Auth {

  constructor(private http: HttpClient, private router: Router) {}

  getFormattedUrl(baseUrl: any) {
    return environment.apiUrl + baseUrl;
  }

  performSave(baseUrl: any, object: Object) {
    return this.http.post(this.getFormattedUrl(baseUrl), object);
  }

  getInfo(baseUrl: any) {
    return this.http.get(this.getFormattedUrl(baseUrl));
  }

  putInfo(baseUrl: any, object: Object) {
    return this.http.put(this.getFormattedUrl(baseUrl), object);
  }

  deleteInfo(baseUrl: any) {
    return this.http.delete(this.getFormattedUrl(baseUrl));
  }

  getById(baseUrl: any, id: any) {
    return this.http.get(`${this.getFormattedUrl(baseUrl)}/${id}`);
  }

  getByQueryParam(baseUrl: any, params: any) {
    return this.http.get<any>(this.getFormattedUrl(baseUrl), { params });
  }

  register(fullName: string, email: string, password: string) {
    return this.performSave('/api/auth/register', { fullName, email, password });
  }

  login(email: string, password: string) {
    return this.performSave('/api/auth/login', { email, password });
  }

  adminLogin(email: string, password: string, adminSecret: string) {
    return this.performSave('/api/auth/admin/login', { email, password, adminSecret });
  }

  adminVerifyOtp(tempToken: string, otp: string) {
    return this.performSave('/api/auth/admin/verify-otp', { tempToken, otp });
  }

  saveRole(role: string): void {
    localStorage.setItem('user_role', role);
  }

  getRole(): string | null {
    return localStorage.getItem('user_role');
  }

  hasAdminRole(): boolean {
    const role = this.getRole();
    return role === 'ADMIN' || role === 'SUPER_ADMIN';
  }

  verifyEmail(token: string) {
    return this.getInfo(`/api/auth/verify-email?token=${token}`);
  }

  refreshToken(refreshToken: string) {
    return this.performSave('/api/auth/refresh', { refreshToken });
  }

  forgotPassword(email: string) {
    return this.performSave('/api/auth/forgot-password', { email });
  }

  resetPassword(token: string, newPassword: string) {
    return this.performSave('/api/auth/reset-password', { token, newPassword });
  }

  saveTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem('access_token', accessToken);
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    } else {
      // No refresh token provided — remove any stale token from a previous session
      localStorage.removeItem('refresh_token');
    }
  }

  getAccessToken(): string | null {
    return localStorage.getItem('access_token');
  }

  getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  }

  isLoggedIn(): boolean {
    return localStorage.getItem('access_token') !== null;
  }

  /** Save display info after login (used by layout & Razorpay prefill) */
  saveUserInfo(name: string, email: string, avatar?: string): void {
    if (name)   localStorage.setItem('user_name',   name);
    if (email)  localStorage.setItem('user_email',  email);
    if (avatar) localStorage.setItem('user_avatar', avatar);
  }

  logout(): void {
    const role = this.getRole();
    this.logoutSilently();
    this.router.navigate([this.loginPathForRole(role)]);
  }

  /** Same token/session teardown as logout(), but leaves navigation to the caller (e.g. to
   *  redirect to login with a returnUrl instead of the default post-logout landing page). */
  logoutSilently(): void {
    const refreshToken = this.getRefreshToken();
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_role');
    localStorage.removeItem('user_name');
    localStorage.removeItem('user_email');
    localStorage.removeItem('user_avatar');

    // Call logout API to invalidate the token server-side (fire & forget)
    if (refreshToken) {
      this.http.post(this.getFormattedUrl('/api/auth/logout'), { refreshToken }).subscribe({
        error: () => {}
      });
    }
  }

  logoutAllDevices(): void {
    const role = this.getRole();
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_role');
    localStorage.removeItem('user_name');
    localStorage.removeItem('user_email');
    localStorage.removeItem('user_avatar');

    this.http.post(this.getFormattedUrl('/api/auth/logout-all'), {}).subscribe({
      error: () => {}
    });

    this.router.navigate([this.loginPathForRole(role)]);
  }

  private loginPathForRole(role: string | null): string {
    if (role === 'COMPANY') return '/auth/company-login';
    if (role === 'TRAINER') return '/auth/trainer-login';
    if (role === 'COORDINATOR') return '/auth/coordinator-login';
    return '/auth/login';
  }

  loginWithGoogle(): void {
    window.location.href = `${environment.apiUrl}/oauth2/authorization/google`;
  }

  loginWithGithub(): void {
    window.location.href = `${environment.apiUrl}/oauth2/authorization/github`;
  }

  /**
   * Shows the Google "One Tap" account-chooser prompt (the small popup in the corner of the
   * page) on logged-out visits to the homepage. Selecting an account posts the resulting
   * credential JWT to the backend, which verifies it and logs the user in the same way as
   * the "Continue with Google" button.
   */
  initGoogleOneTap(): void {
    if (this.isLoggedIn()) return;

    this.http.get<any>(this.getFormattedUrl('/api/auth/google/client-id')).subscribe({
      next: (res: any) => {
        const clientId = res?.data?.clientId;
        if (!clientId) return;
        this.waitForGoogleScript(() => this.promptGoogleOneTap(clientId));
      },
      error: () => {},
    });
  }

  private waitForGoogleScript(onReady: () => void, attemptsLeft = 20): void {
    if (typeof google !== 'undefined' && google?.accounts?.id) {
      onReady();
      return;
    }
    if (attemptsLeft <= 0) return;
    setTimeout(() => this.waitForGoogleScript(onReady, attemptsLeft - 1), 250);
  }

  private promptGoogleOneTap(clientId: string): void {
    google.accounts.id.initialize({
      client_id: clientId,
      callback: (response: any) => this.handleGoogleOneTapCredential(response),
      auto_select: false,
      cancel_on_tap_outside: true,
    });
    google.accounts.id.prompt();
  }

  private handleGoogleOneTapCredential(response: any): void {
    const credential = response?.credential;
    if (!credential) return;

    this.performSave('/api/auth/google/one-tap', { credential }).subscribe({
      next: (res: any) => {
        this.saveTokens(res.data.accessToken, res.data.refreshToken);
        this.saveUserInfo(
          res.data.user?.fullName ?? '',
          res.data.user?.email    ?? '',
          res.data.user?.profilePicture ?? ''
        );
        this.router.navigateByUrl('/dashboard');
      },
      error: () => {},
    });
  }
}